"use client";

/**
 * Native Android shell helpers (Capacitor plugins via window.Capacitor).
 * No-ops on web / when a plugin is missing.
 */

import {
  capacitorPlugin,
  isNativeAndroid,
} from "@/studio/lib/capacitorBridge";

export { isNativeAndroid };

type NativeListener = { remove: () => Promise<void> };

const FCM_TOKEN_KEY = "studio.fcmToken";
let pendingFcmToken: string | null = null;
const fcmTokenListeners = new Set<(token: string) => void>();

/** Stash FCM token until Convex auth can save it. */
export function setPendingFcmToken(token: string) {
  const value = token.trim();
  if (!value) return;
  pendingFcmToken = value;
  try {
    sessionStorage.setItem(FCM_TOKEN_KEY, value);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(
    new CustomEvent("studio:fcm-token", { detail: { token: value } }),
  );
  for (const listener of fcmTokenListeners) {
    try {
      listener(value);
    } catch {
      /* ignore */
    }
  }
}

export function getPendingFcmToken(): string | null {
  if (pendingFcmToken) return pendingFcmToken;
  try {
    return sessionStorage.getItem(FCM_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function subscribeFcmToken(listener: (token: string) => void): () => void {
  fcmTokenListeners.add(listener);
  const existing = getPendingFcmToken();
  if (existing) {
    try {
      listener(existing);
    } catch {
      /* ignore */
    }
  }
  const onWindow = (event: Event) => {
    const token = (event as CustomEvent<{ token?: string }>).detail?.token;
    if (token) listener(token);
  };
  window.addEventListener("studio:fcm-token", onWindow);
  return () => {
    fcmTokenListeners.delete(listener);
    window.removeEventListener("studio:fcm-token", onWindow);
  };
}

declare global {
  interface Window {
    __yatisharaSaveFcm?: (token: string) => void;
  }
}

if (typeof window !== "undefined") {
  window.__yatisharaSaveFcm = (token: string) => setPendingFcmToken(token);
}

function plugin(name: string) {
  if (!isNativeAndroid()) return null;
  return capacitorPlugin(name);
}

/** Light impact on Android; web keeps navigator.vibrate. */
export async function nativeImpact(style: "light" | "medium" | "heavy" = "light") {
  const ms = style === "heavy" ? 16 : style === "medium" ? 12 : 8;
  if (!isNativeAndroid()) {
    try {
      navigator.vibrate?.(ms);
    } catch {
      /* ignore */
    }
    return;
  }
  const haptics = plugin("Haptics");
  // Capacitor ImpactStyle values are uppercase.
  const impactStyle =
    style === "heavy" ? "HEAVY" : style === "medium" ? "MEDIUM" : "LIGHT";
  if (!haptics?.impact) {
    try {
      navigator.vibrate?.(ms);
    } catch {
      /* ignore */
    }
    return;
  }
  try {
    await haptics.impact({ style: impactStyle });
  } catch {
    try {
      await haptics.vibrate?.({ duration: ms });
    } catch {
      try {
        navigator.vibrate?.(ms);
      } catch {
        /* ignore */
      }
    }
  }
}

export async function nativeShare(opts: {
  title?: string;
  text?: string;
  url?: string;
  dialogTitle?: string;
}): Promise<boolean> {
  const title = opts.title?.trim() || undefined;
  const text = opts.text?.trim() || undefined;
  const url = opts.url?.trim() || undefined;
  if (!title && !text && !url) return false;

  if (isNativeAndroid()) {
    const share = plugin("Share");
    if (share?.share) {
      try {
        await share.share({
          title,
          text,
          url,
          dialogTitle: opts.dialogTitle,
        });
        return true;
      } catch {
        /* fall through */
      }
    }
  }

  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share({ title, text, url });
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

export async function setNativeKeepAwake(on: boolean) {
  const keep = plugin("KeepAwake");
  if (!keep) return;
  try {
    if (on && keep.keepAwake) await keep.keepAwake();
    if (!on && keep.allowSleep) await keep.allowSleep();
  } catch {
    /* ignore */
  }
}

export type NativeIncomingCall = {
  callId: string;
  callerName: string;
  hasVideo?: boolean;
  handle?: string;
  conversationId?: string;
};

let activeNativeCallId: string | null = null;

/**
 * Closed/background ring is native {@code StudioIncomingCallNotifier} from FCM.
 * Capgo Call Kit is not used — direct WebView load has no Cap bridge.
 * Foreground ringing stays in-app ({@code studioDmCallRingtone}).
 */
export async function showNativeIncomingCall(_opts: NativeIncomingCall) {
  return false;
}

export async function endNativeIncomingCall(callId?: string) {
  const id = callId || activeNativeCallId;
  if (activeNativeCallId === id) activeNativeCallId = null;
}

export function listenNativeIncomingCall(handlers: {
  onAccepted?: (callId: string, extra?: Record<string, unknown>) => void;
  onDeclined?: (callId: string, extra?: Record<string, unknown>) => void;
  onEnded?: (callId: string) => void;
  onTimedOut?: (callId: string) => void;
}): () => void {
  const kit = plugin("IncomingCallKit");
  if (!kit?.addListener) return () => {};
  const removers: Array<() => void | Promise<void>> = [];
  const bind = (event: string, fn: (payload: Record<string, unknown>) => void) => {
    void kit
      .addListener?.(event, (payload) => {
        fn(payload ?? {});
      })
      .then((handle: NativeListener) => removers.push(() => handle.remove()));
  };
  bind("callAccepted", (p) => {
    const call = (p.call as Record<string, unknown> | undefined) ?? p;
    const callId = String(call.callId ?? "");
    const extra = (call.extra as Record<string, unknown> | undefined) ?? undefined;
    if (!callId) return;
    handlers.onAccepted?.(callId, extra);
  });
  bind("callDeclined", (p) => {
    const call = (p.call as Record<string, unknown> | undefined) ?? p;
    const callId = String(call.callId ?? "");
    const extra = (call.extra as Record<string, unknown> | undefined) ?? undefined;
    if (!callId) return;
    handlers.onDeclined?.(callId, extra);
  });
  bind("callEnded", (p) => {
    const call = (p.call as Record<string, unknown> | undefined) ?? p;
    const callId = String(call.callId ?? "");
    if (callId) handlers.onEnded?.(callId);
  });
  bind("callTimedOut", (p) => {
    const call = (p.call as Record<string, unknown> | undefined) ?? p;
    const callId = String(call.callId ?? "");
    if (callId) handlers.onTimedOut?.(callId);
  });
  return () => {
    for (const remove of removers) {
      try {
        void remove();
      } catch {
        /* ignore */
      }
    }
  };
}

export async function configureNativeKeyboard() {
  // Capacitor Keyboard.resize / setResizeMode are iOS-only.
  // On Android the Activity uses adjustResize; resizeOnFullScreen is set in
  // capacitor.config.ts for StatusBar overlay cases.
  if (!isNativeAndroid()) return;
}

export type NativeNetworkStatus = {
  connected: boolean;
  connectionType?: string;
};

export async function getNativeNetworkStatus(): Promise<NativeNetworkStatus | null> {
  const network = plugin("Network");
  if (!network?.getStatus) return null;
  try {
    const status = (await network.getStatus()) as {
      connected?: boolean;
      connectionType?: string;
    };
    return {
      connected: Boolean(status.connected),
      connectionType: status.connectionType,
    };
  } catch {
    return null;
  }
}

export function listenNativeNetwork(
  onChange: (status: NativeNetworkStatus) => void,
): () => void {
  const network = plugin("Network");
  if (!network?.addListener) return () => {};
  let remove: (() => void | Promise<void>) | null = null;
  void network
    .addListener("networkStatusChange", (status) => {
      onChange({
        connected: Boolean(status.connected),
        connectionType: String(status.connectionType ?? ""),
      });
    })
    .then((handle: NativeListener) => {
      remove = () => handle.remove();
    });
  return () => {
    try {
      void remove?.();
    } catch {
      /* ignore */
    }
  };
}

export type NativePushHandlers = {
  onToken?: (token: string) => void;
  onNotification?: (data: Record<string, unknown>) => void;
  onAction?: (data: Record<string, unknown>) => void;
};

let nativeCallChannelPrepared = false;

/**
 * Drop studio_calls only when push registration locked it to the default
 * notification sound. Call Kit recreates it with the handset ringtone.
 * A custom or already-ringtone channel is left alone.
 */
async function prepareNativeCallChannel() {
  if (nativeCallChannelPrepared) return;
  nativeCallChannelPrepared = true;
  const push = plugin("PushNotifications");
  if (!push?.listChannels || !push?.deleteChannel) return;
  try {
    const listed = (await push.listChannels()) as {
      channels?: Array<{ id?: string; sound?: string | null }>;
    };
    const channel = listed.channels?.find((item) => item.id === "studio_calls");
    const sound = String(channel?.sound ?? "");
    if (!sound.includes("notification_sound")) return;
    await push.deleteChannel({ id: "studio_calls" });
  } catch {
    /* older shell or a missing channel */
  }
}

/** Android notification channels. createChannel is a no-op when the plugin lacks it. */
async function ensureNativePushChannels(push: {
  // CapacitorPlugin types don't surface dynamically-registered methods, so the
  // caller passes a loose shape and every use below is feature-detected.
  [key: string]: unknown;
  createChannel?: (options?: Record<string, unknown>) => Promise<unknown>;
}) {
  if (typeof push.createChannel !== "function") return;
  // studio_calls is owned by Incoming Call Kit so the channel sound is a
  // ringtone. Creating it here locks notification usage and the kit cannot
  // change the sound later.
  const channels: Array<Record<string, unknown>> = [
    {
      id: "studio_alerts",
      name: "Studio",
      description: "Studio alerts",
      importance: 4,
    },
  ];
  for (const channel of channels) {
    try {
      await push.createChannel(channel);
    } catch {
      /* older shell or API < 26 — registration still proceeds */
    }
  }
}

export async function registerNativePush(
  handlers: NativePushHandlers = {},
): Promise<() => void> {
  const push = plugin("PushNotifications");
  if (!push) return () => {};

  const removers: Array<() => void | Promise<void>> = [];
  const bind = (event: string, fn: (payload: Record<string, unknown>) => void) => {
    void push
      .addListener?.(event, (payload) => fn(payload ?? {}))
      .then((handle: NativeListener) => removers.push(() => handle.remove()));
  };

  bind("registration", (p) => {
    const token = String(p.value ?? p.token ?? "");
    if (token) handlers.onToken?.(token);
  });
  bind("registrationError", () => {
    /* no-op without google-services.json */
  });
  bind("pushNotificationReceived", (p) => {
      const data =
        (p.data as Record<string, unknown>) ||
        (p.notification as Record<string, unknown>) ||
        p;
      handlers.onNotification?.(data);
      // Raise call kit when a closed app is woken by a call ping.
      const kind = String(data.kind ?? "");
      const callId = String(data.callSessionId ?? data.callId ?? "");
      if (kind === "dm_call" && callId) {
        void showNativeIncomingCall({
          callId,
          callerName: String(data.title || data.callerName || "Studio"),
          hasVideo: String(data.mode || "") === "video",
          conversationId: String(data.conversationId || ""),
        });
      }
    });
  bind("pushNotificationActionPerformed", (p) => {
    const notification = (p.notification as Record<string, unknown>) || {};
    const data =
      (notification.data as Record<string, unknown>) ||
      (p.data as Record<string, unknown>) ||
      {};
    handlers.onAction?.(data);
  });

  try {
    if (push.requestPermissions) {
      const perm = (await push.requestPermissions()) as { receive?: string };
      if (perm?.receive === "denied") return () => {};
    }
    await ensureNativePushChannels(push);
    if (push.register) await push.register();
  } catch {
    /* google-services.json missing — push stays a no-op */
  }

  return () => {
    for (const remove of removers) {
      try {
        void remove();
      } catch {
        /* ignore */
      }
    }
  };
}
